// Wraps the tasting form on the guest event page. Submit is intercepted
// (before Turbo's own handler, which respects preventDefault) and the form
// is kept in localStorage as a GuestNote whose draft is the legacy
// TastingDraft: camelCase, lists as arrays, booleans, price in cents. Then
// back to the lineup -- or, at the third note, to the sign-in prompt.
import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { saveGuestNote, hasReachedGuestLimit, hasGuestNoteForEventWine } from "../guest/storage"

const STORAGE_FULL = "Storage full — please sign in to save your notes."

const SCALARS = [
  "color", "color_intensity", "clarity", "nose_condition", "nose_fault", "nose_intensity", "nose_development",
  "sweetness", "acidity", "tannin", "alcohol", "body", "mousse", "flavor_intensity", "finish_length",
  "price_category", "readiness"
]
const LISTS = ["nose_descriptors", "flavor_descriptors"]
const BOOLEANS = ["blic_balance", "blic_length", "blic_intensity", "blic_complexity", "bang_for_buck"]

function camel(name) {
  return name.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

export default class extends Controller {
  static targets = ["error"]
  static values = {
    eventId: String,
    eventWineId: String,
    wineId: String,
    joinCode: String,
    eventUrl: String,
    signInUrl: String
  }

  connect() {
    // The lineup already refuses these taps; a stale tab gets the same answer.
    if (hasReachedGuestLimit()) Turbo.visit(this.signInUrlValue)
    else if (hasGuestNoteForEventWine(this.eventWineIdValue)) Turbo.visit(this.eventUrlValue)
  }

  save(event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement) || form.dataset.guest !== "true") return
    event.preventDefault()

    const note = {
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      eventId: this.eventIdValue,
      eventWineId: this.eventWineIdValue,
      wineId: this.wineIdValue,
      joinCode: this.joinCodeValue,
      draft: this.draft(new FormData(form))
    }

    if (!saveGuestNote(note)) {
      this.errorTarget.textContent = STORAGE_FULL
      this.errorTarget.hidden = false
      window.scrollTo({ top: 0 })
      // submitting() already set leaving = true; no Turbo submission will
      // ever end to clear it, so tell the discard guard to rearm itself.
      form.dispatchEvent(new CustomEvent("guest-note:failed", { bubbles: true }))
      return
    }

    Turbo.visit(hasReachedGuestLimit() ? this.signInUrlValue : this.eventUrlValue)
  }

  draft(data) {
    const field = (name) => data.get(`tasting_note[${name}]`)
    const draft = {}
    SCALARS.forEach((name) => { draft[camel(name)] = (field(name) || "").toString().trim() || null })
    LISTS.forEach((name) => {
      draft[camel(name)] = data.getAll(`tasting_note[${name}]`)
        .flatMap((value) => value.toString().split(","))
        .map((value) => value.trim())
        .filter(Boolean)
    })
    BOOLEANS.forEach((name) => {
      // Rails check boxes post a hidden "0" then "1" when ticked.
      const values = data.getAll(`tasting_note[${name}]`)
      draft[camel(name)] = values.length > 0 && values[values.length - 1] === "1"
    })
    draft.finishNotes = (field("finish_notes") || "").toString()
    draft.starRating = Number(field("star_rating")) || 3
    const price = parseFloat((field("price") || "").toString().replace(/[,$]/g, ""))
    draft.priceCents = Number.isFinite(price) ? Math.round(price * 100) : null
    return draft
  }
}
